import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { OFFICES, FIRM_PHONE, PEAK_PHONE, MAIN_OFFICE_PHONE, FIRM_EMAIL, FIRM_DISCIPLINES } from "@/lib/content";
import SEO from "@/components/SEO";
import { breadcrumbSchema } from "@/lib/schemas";
import LSCMonogram from "@/components/LSCMonogram";
import StateOfFirmSubscribe from "@/components/StateOfFirmSubscribe";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Received. For a time-sensitive matter, call (972) 712-1515.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ivory" data-testid="contact-page">
      <SEO
        title="Contact — Write to the Firm"
        description="Reach Lone Star Counsel by telephone or correspondence. Principal office in Frisco — (972) 712-1515. Toll-free statewide — (888) 517-4575. Five Texas offices."
        path="/contact"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
      />
      <section className="pt-40 pb-20 max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex items-start gap-6" data-testid="contact-letterhead">
          <LSCMonogram size={72} tone="navy" />
          <div>
            <div className="kicker">Correspondence</div>
            <h1 className="mt-6 font-display text-6xl md:text-8xl leading-[0.95]">
              Write to the<br/>
              <span className="italic font-cormorant text-oxblood">firm.</span>
            </h1>
          </div>
        </div>
        <p className="mt-10 max-w-2xl text-lg text-slate2 font-light leading-relaxed">
          For general correspondence, write to
          <a href={FIRM_EMAIL.href} className="link-underline font-serif italic text-oxblood mx-1">{FIRM_EMAIL.display}</a>
          or use the form below. For prospective engagements,
          <Link to="/intake" className="link-underline font-serif italic text-oxblood mx-1">request a conference</Link>
          instead. All communication is held in confidence.
        </p>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="space-y-8 hairline pt-10" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <label className="block">
                  <div className="field-label mb-2">Name</div>
                  <input className="field" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                </label>
                <label className="block">
                  <div className="field-label mb-2">Email</div>
                  <input className="field" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
                </label>
              </div>
              <label className="block">
                <div className="field-label mb-2">Subject</div>
                <input className="field" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
              </label>
              <label className="block">
                <div className="field-label mb-2">Message</div>
                <textarea className="field" required rows={8} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" placeholder="A few lines about the nature of your correspondence." />
              </label>
              <div className="hairline pt-6 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-slate2/70 uppercase tracking-[0.2em] max-w-md">
                  Submission of this form does not create an attorney–client relationship.
                </p>
                <button type="submit" disabled={sending} className="btn-primary disabled:opacity-40" data-testid="contact-submit">
                  {sending ? "Sending…" : "Send Correspondence"}
                </button>
              </div>
              {done && (
                <div className="text-oxblood font-serif italic" data-testid="contact-success">
                  &mdash; Received. For a time-sensitive matter, call{" "}
                  <a href="tel:+19727121515" className="underline underline-offset-4 hover:text-brass" data-testid="contact-success-tel">
                    (972) 712-1515
                  </a>.
                </div>
              )}
            </form>
          </div>

          {/* Offices */}
          <aside className="lg:col-span-4 lg:col-start-9">
            <div className="kicker">By Telephone</div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.36em] text-oxblood">{FIRM_DISCIPLINES}</div>
            <div className="mt-6 hairline pt-6">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate2/60">Principal Office · Frisco</div>
              <a href={MAIN_OFFICE_PHONE.href} className="mt-2 block font-display text-4xl md:text-5xl text-navy hover:text-oxblood transition" data-testid="contact-phone-main-office">
                {MAIN_OFFICE_PHONE.display}
              </a>
            </div>
            <div className="mt-6 hairline pt-6">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate2/60">Toll-Free · Statewide</div>
              <a href={FIRM_PHONE.href} className="mt-2 block font-display text-3xl md:text-4xl text-navy hover:text-oxblood transition" data-testid="contact-phone-main">
                {FIRM_PHONE.display}
              </a>
            </div>
            <div className="mt-6 hairline pt-6 bg-brass/[0.06] -mx-4 px-4 py-5">
              <div className="text-[10px] uppercase tracking-[0.28em] text-oxblood">PEAK American Financial</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate2/60 mt-1">Clients &amp; Representatives Only</div>
              <a href={PEAK_PHONE.href} className="mt-3 block font-cormorant italic text-3xl md:text-4xl text-oxblood hover:text-navy transition" data-testid="contact-phone-peak">
                {PEAK_PHONE.display}
              </a>
            </div>

            <div className="mt-6 hairline pt-6">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate2/60">By Correspondence</div>
              <a href={FIRM_EMAIL.href} className="mt-2 block font-display text-2xl md:text-3xl text-navy hover:text-oxblood transition break-all" data-testid="contact-email">
                {FIRM_EMAIL.display}
              </a>
            </div>

            <div className="kicker mt-14">Offices Across Texas</div>
            <div className="mt-6 space-y-8">
              {OFFICES.map((o) => (
                <Link
                  to={`/locations/${o.slug}`}
                  key={o.region}
                  className="block hairline pt-6 group"
                  data-testid={`office-${o.slug}`}
                >
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="font-serif text-2xl md:text-3xl text-navy group-hover:text-oxblood transition">{o.region}</div>
                    {o.label === "Principal Offices" && (
                      <span className="text-[10px] tracking-[0.28em] uppercase text-oxblood/80 font-sans">Principal</span>
                    )}
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.22em] text-slate2/60">{o.serves}</div>
                  <div className="mt-3 text-slate2 font-light leading-relaxed">{o.line1}</div>
                  <div className="text-slate2 font-light">{o.line2}</div>
                  {o.note && (
                    <div className="mt-2 font-cormorant italic text-oxblood/80 text-sm">{o.note}</div>
                  )}
                  <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-brass group-hover:text-oxblood transition">Visit Office →</div>
                </Link>
              ))}
            </div>
            <div className="mt-12">
              <Link to="/intake" className="btn-brass" data-testid="contact-intake-link">Request a Conference</Link>
            </div>
          </aside>
        </div>
      </section>

      {/* State of the Firm subscribe — closer opt-in */}
      <StateOfFirmSubscribe variant="dark" />
    </div>
  );
}
